import { EMAIL_PARAM, INVITE_PARAM, TOKEN_PARAM } from './EsConstants';
import { esDevice, esDeviceWatch } from './EsDevice';

// New Relic Browser (RUM), dev and prod only, and only because their builds are handed
// an application. The five NEW_RELIC_* values are build-time like GA_MEASUREMENT_ID
// (webpack.config.js): blank, as on a laptop, in CI, in jest, and in the AQA bundle,
// means index.js never imports the agent and webpack leaves it out of the build.
//
// This module imports nothing from @newrelic so that EsParent can hold it in every
// build at no cost; EsRumAgent.js is the half that carries the agent.
//
// The browser key is public by design: it ships in every page and can only send.
// It is NOT esback's NEW_RELIC_LICENSE_KEY, which is a secret ingest key.

const ID_PATTERN = /^\d{1,20}$/;
const KEY_PATTERN = /^NRJS-[A-Za-z0-9]{10,40}$/;

// The US data center, which account 8652120 is on. Named rather than left to the
// agent's default so the CSP in nginx.headers.conf can list exactly this host.
export const ES_RUM_BEACON = 'bam.nr-data.net';

// The agent strips query strings from the URLs it records (1.323.0's cleanURL), but a
// URL quoted inside an error message is not stripped, and the reset and opt-out links
// carry a token, the register link an address. So the values are masked wherever they
// appear, and a bare address in a message with them. `code` is an OAuth authorization
// code, which a quoted callback URL would carry.
const SECRET_PARAM_PATTERN = new RegExp(
    String.raw`([?&#;](?:${ [ TOKEN_PARAM, EMAIL_PARAM, INVITE_PARAM, 'code' ].join( '|' ) })=)[^&#\s'"]*`, 'gi' );
// The domain as labels each ended by a dot, then a last label of letters: no two parts can match the same text.
const EMAIL_PATTERN = /[\w.%+-]+@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}\b/g;

// Null unless every value is present and well formed, and never under automation: the
// nightly AQA legs and the zdship gate drive dev, and the post-deploy check drives prod,
// all through Selenium, which sets navigator.webdriver (as EsAnalytics refuses it too).
export const esRumConfig = () => {
    if ( typeof navigator !== 'undefined' && navigator.webdriver ) return null;

    // Each name spelled out: DefinePlugin replaces `process.env.NAME` and never
    // `process.env[ name ]`, which would read empty in the bundle and start nothing.
    const aAppId = process.env.NEW_RELIC_BROWSER_APP_ID || '';
    const aKey = process.env.NEW_RELIC_BROWSER_LICENSE_KEY || '';
    const aAccount = process.env.NEW_RELIC_ACCOUNT_ID || '';
    // A browser app's agent ID is its application ID, and an account that is not a
    // sub-account is its own trust key, so both may be left blank.
    const aAgent = process.env.NEW_RELIC_BROWSER_AGENT_ID || aAppId;
    const aTrust = process.env.NEW_RELIC_TRUST_KEY || aAccount;

    if ( ![ aAppId, aAccount, aAgent, aTrust ].every( ( pId ) => ID_PATTERN.test( pId ) ) ) return null;
    if ( !KEY_PATTERN.test( aKey ) ) return null;

    return {
        info: {
            beacon: ES_RUM_BEACON,
            errorBeacon: ES_RUM_BEACON,
            licenseKey: aKey,
            applicationID: aAppId,
        },
        loader_config: {
            accountID: aAccount,
            trustKey: aTrust,
            agentID: aAgent,
            licenseKey: aKey,
            applicationID: aAppId,
        },
        init: {
            // Replay and trace are session-scoped, so the agent keeps its session id in
            // localStorage (NRBA_SESSION; no cookie), lapsing after 30 minutes idle or 4
            // hours. The privacy page says so.
            privacy: { cookies_enabled: true },
            // Same-origin only. /api is the page's own origin, so esback's spans join
            // the trace with no allowed_origins; listing LaunchDarkly, S3, or Stripe
            // would send them trace headers and fail their CORS preflights.
            distributed_tracing: { enabled: true },
            // Enabled here, recorded only when New Relic says so: the replay's and the
            // trace's mode come from the RUM response's srs and sts flags, which follow
            // the sampling set in New Relic, so no init rate is given (1.323.0 reads
            // sampling_rate only to report it). Every word, every input, and every image,
            // video, and canvas is hidden in the browser before anything is sent. The
            // first two are the agent's defaults, pinned so a library change cannot
            // loosen them unseen; block_selector adds to its [data-nr-block].
            session_replay: {
                enabled: true,
                mask_all_inputs: true,
                mask_text_selector: '*',
                block_selector: 'img, video, canvas',
            },
            session_trace: { enabled: true },
            // Not bundled (EsRumAgent.js); stated so a later switch to the full
            // BrowserAgent cannot turn them on unseen.
            generic_events: { enabled: false },
            user_actions: { enabled: false },
            logging: { enabled: false },
            ajax: { deny_list: [ ES_RUM_BEACON ] },
            obfuscate: [
                { regex: SECRET_PARAM_PATTERN, replacement: '$1[masked]' },
                { regex: EMAIL_PATTERN, replacement: '[email]' },
            ],
        },
    };
};

let sAgent = null;
let sRoute = '';

// EsDevice's snapshot as custom attributes. 1.323.0 copies info.jsAttributes into every
// PageView, PageViewTiming, BrowserInteraction, AjaxRequest, and JavaScriptError as it
// sends them, so one call covers them all. `persist` stays false: it only writes the
// pair to the agent's session storage for the next page load, which cookies_enabled
// false turns off, and every load sets them afresh anyway. A null is left out: the agent
// routes a null through that same storage path, and what a browser cannot say on one
// read it cannot say on the next.
export const esRumDevice = ( pDevice ) => {
    if ( !sAgent || typeof sAgent.setCustomAttribute !== 'function' ) return;
    Object.entries( pDevice )
        .filter( ( [ , pValue ] ) => pValue !== null && pValue !== undefined )
        .forEach( ( [ pName, pValue ] ) => sAgent.setCustomAttribute( pName, pValue ) );
};

// The first route renders before the agent's chunk arrives, so its name waits here.
// The device attributes go on in the same tick as `new Agent`, before the PageView
// feature's lazy chunk has loaded and sent the page view.
export const esRumAttach = ( pAgent ) => {
    sAgent = pAgent;
    if ( sAgent && sRoute ) sAgent.setCurrentRouteName( sRoute );
    if ( sAgent ) {
        esRumDevice( esDevice() );
        esDeviceWatch( esRumDevice );
    }
};

// Groups soft navigations by route rather than by URL, so /view/console/12 and
// /view/console/40 are one page in New Relic. The name is EsAnalytics' scrubbed path.
// The device is read again here because the first read beats EsThemeContext's effect,
// so appScheme is index.html's default until a route has rendered.
export const esRumRoute = ( pName ) => {
    sRoute = pName || '';
    if ( sAgent && sRoute ) sAgent.setCurrentRouteName( sRoute );
    if ( sAgent ) esRumDevice( esDevice() );
};

// An error the app caught and drew for the reader, sent as a JavaScriptError so New Relic
// alerts on it like an uncaught one (EsFetch's answer that is not JSON). No agent, no call:
// a laptop, jest, and the AQA bundle send nothing. The attributes are names and numbers only.
export const esRumError = ( pError, pAttributes ) => {
    if ( !sAgent || typeof sAgent.noticeError !== 'function' ) return;
    sAgent.noticeError( pError, pAttributes );
};
